import { NP } from "@/lib/ldc01";

// Derives the 10 verification gates ONLY from server evidence. Missing evidence is never PASS.
const g = (key, label, state, evidence) => ({ key, label, state, evidence });
const gate = (report, name) => (report?.gates || {})[name];
const fromGate = (v) => (v === "PASS" ? "PASS" : v ? "FAIL" : "PENDING");

const approvalGate = (a) => (!a ? ["NOT VERIFIED", NP]
  : a.status === "PENDING" ? ["PENDING", "No Owner approval record on server"]
  : a.status === "MALFORMED" ? ["FAIL", `Approval record invalid: ${a.reason}`]
  : ["NOT VERIFIED", `Record present (${a.approved_by}, ${a.approved_at}) · ${a.reason}`]);

function restoreGate(evidence, dryJob) {
  if (!evidence) return ["NOT VERIFIED", NP];
  if (!dryJob?.id) return ["PENDING", "No dry-run job on server"];
  const it = (evidence.items || []).find((x) => x.dry_run_job_id === dryJob?.id);
  if (!it) return ["PENDING", "No backup evidence recorded for the latest dry-run"];
  return it.state === "VERIFIED_IDENTICAL"
    ? ["PASS", `Evidence ${it.evidence_id} · ${it.verified_scope} · archive ${String(it.archive_sha256).slice(0, 12)}…`]
    : ["FAIL", `Evidence ${it.evidence_id} is ${it.state}`];
}

function codeGate(approvals, dryJob) {
  if (!approvals) return ["NOT VERIFIED", NP];
  if (!dryJob?.id) return ["PENDING", "No dry-run job on server"];
  const it = (approvals.items || []).find((x) => x.dry_run_job_id === dryJob?.id);
  if (!it) return ["PENDING", "No approval code issued for the latest dry-run"];
  if (it.state === "READY" && it.issued_to_me && it.manifest_match) return ["PASS", `Ready · expires ${it.expires_at} · issued to you`];
  if (it.state === "PENDING") return ["PENDING", "Code issued; dry-run not yet succeeded"];
  return ["FAIL", `Code ${String(it.state || "malformed").toLowerCase()}${it.issued_to_me ? "" : " · issued to another operator"}`];
}

export function deriveGates(status, dryJob, resultJob, ev = {}) {
  const rep = dryJob?.report || null;
  const fpSum = Object.values(rep?.target_fingerprints || {}).reduce((a, v) => a + (v?.count || 0), 0);
  const total = ev.manifest?.integrity === "PASS" ? ev.manifest.total : null;
  const dryState = !dryJob || dryJob.status === "running" ? "PENDING"
    : dryJob.status === "succeeded" && String(rep?.result || "").startsWith("DRY-RUN OK") ? "PASS" : "FAIL";
  const tr = gate(rep, "targets_and_references");
  const fg = gate(rep, "fg_reconciliation");
  const exact = !rep ? "PENDING" : total == null ? "NOT VERIFIED"
    : tr === "PASS" && rep.total_targets === total && fpSum === total ? "PASS" : fromGate(tr) === "PENDING" ? "PENDING" : "FAIL";
  const prot = !rep ? "PENDING" : tr === "PASS" && fg === "PASS" && rep.reference_hit_count === 0 ? "PASS" : (fg || tr) ? "FAIL" : "PENDING";
  const env = ev.environment;
  const coord = ev.coordination;
  const rr = coord?.recovery_required || (resultJob?.report?.recovery_required === true ? { run_id: resultJob.report.run_id } : null);
  const [aS, aE] = approvalGate(ev.approval);
  const [rS, rE] = restoreGate(ev.evidence, dryJob);
  const [cS, cE] = codeGate(ev.approvals, dryJob);
  return [
    g("owner_manifest", "Owner manifest approval", aS, aE),
    g("backup_provenance", "Verified Live backup provenance", "NOT VERIFIED", "Origin is operator-declared; chain-of-custody record not yet available (Phase C)"),
    g("restore_verification", "Independent backup restore verification", rS, rE),
    g("db_identity", "Current database identity", !env ? "NOT VERIFIED" : env.overall === "FAIL" ? "FAIL" : "NOT VERIFIED",
      env ? `${env.connected_db} · setting ${env.setting || "absent"} · record ${env.record_present ? "present" : "absent"} · ${env.overall}` : NP),
    g("exact_id", "Exact-ID reconciliation", exact,
      rep ? `targets_and_references: ${tr || NP} · dry-run total ${rep.total_targets ?? NP} · fingerprints ${fpSum} · manifest ${total ?? NP}` : "No scan / dry-run evidence on server"),
    g("dry_run", "Live dry-run result", dryState, dryJob ? `${dryJob.id} · ${rep?.result || dryJob.status}` : "No dry-run job on server"),
    g("protected", "Protected-record checks", prot,
      rep ? `fg_reconciliation: ${fg || NP} · reference hits: ${rep.reference_hit_count ?? NP}` : "No scan / dry-run evidence on server"),
    g("approval", "Approval validity", cS, cE),
    g("lock", "Distributed job-lock readiness", !coord ? "NOT VERIFIED" : coord.lock_state === "HELD" ? "FAIL" : "NOT VERIFIED",
      coord ? `${coord.lock_state === "HELD" ? `Held by ${coord.lock.job_id} (${coord.lock.age_s}s)` : "Free"} · ${coord.heartbeat}` : NP),
    g("recovery", "Recovery readiness", rr ? "FAIL" : "NOT VERIFIED",
      rr ? `RECOVERY_REQUIRED for ${rr.run_id}` : coord ? `No marker · ${coord.stale_lock_release}` : NP),
  ];
}

export const allMandatoryPass = (gates) => gates.length > 0 && gates.every((x) => x.state === "PASS");
